import type React from 'react';
import { useCallback } from 'react';
import styled from 'styled-components';

import type { AddressProps as CommonAddressProps } from '@wecasa/customer/src/components/basket/types';

import { translate } from '@wecasa/i18n';

import Accordion from '@wecasa/web-ui/src/components/accordion/accordion';
import Text from '@wecasa/web-ui/src/components/base-text/variants/text';
import Checkbox from '@wecasa/web-ui/src/components/checkbox/checkbox';
import Input from '@wecasa/web-ui/src/components/input/input';
import Separator from '@wecasa/web-ui/src/components/separator/separator';
import type { WithClassNameProps } from '@wecasa/web-ui/src/components/types';

import useBasketSection from '../../../../../hooks/use-basket-section';
import { route } from '../../../../../steps/steps';

const t = translate('basket');

const StyledAccordion = styled(Accordion)<{
  $canEditAddressDetails: boolean;
}>`
  margin-bottom: ${({ $canEditAddressDetails }) => ($canEditAddressDetails ? '5px' : '20px')};
`;

const Label = styled.label`
  display: inline-flex;
  align-items: center;
  margin: 0;
`;

const Title = styled(Text)`
  margin-left: 15px;
`;

const StyledInput = styled(Input)`
  margin-top: 10px;
`;

type AddressProps = CommonAddressProps & WithClassNameProps;

const Address = ({
  address,
  hasTopSeparator = true,
  addressDetails,
  setAddressDetails,
  hasFreeParking = false,
  toggleHasFreeParking,
  className,
}: AddressProps) => {
  const addressToDisplay = address?.address;
  const persistedAddressDetails = address?.address_details;
  const { isOpen, isVisible } = useBasketSection({ data: addressToDisplay, sectionLocations: [route('root')] });

  const canEditAddressDetails = !!setAddressDetails && !!toggleHasFreeParking;

  // If the details can be edited but we do not have persisted details yet,
  // the accordion should not be toggable. Like this the input is always visible.
  const withToggle = !canEditAddressDetails || !!persistedAddressDetails;

  const onDetailsChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (setAddressDetails) setAddressDetails(event.target.value);
    },
    [setAddressDetails],
  );

  if (!isVisible) return null;

  return (
    <>
      {hasTopSeparator && <Separator />}
      <StyledAccordion
        icon='pointer-filled'
        iconLabel={t('.address_icon_label')}
        title={addressToDisplay || t('.address')}
        isOpen={isOpen}
        withToggle={withToggle}
        $canEditAddressDetails={canEditAddressDetails}
        className={className}
      >
        {canEditAddressDetails ? (
          <>
            <Label>
              <Checkbox id='checkbox' name='parking' onChange={toggleHasFreeParking} checked={hasFreeParking} />
              <Title>{t('.free_parking')}</Title>
            </Label>
            <StyledInput
              name='address_details'
              value={addressDetails || undefined}
              label={t('inputs.address_details.placeholder')}
              id='address_details'
              onChange={onDetailsChange}
            />
          </>
        ) : (
          <Text>{persistedAddressDetails || t('.address_no_additional_info')}</Text>
        )}
      </StyledAccordion>
    </>
  );
};

export default Address;
